/*
 * OnlineNowModal — Command Center popup showing every session active in
 * the last 5 minutes with header stats + per-session detail table.
 * Opens when the "Online Now" MetricCard is clicked.
 *
 * Backend: GET /api/analytics/command/online-now (admin-only)
 * Auto-refresh: 30s while modal is open, paused when closed.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { authAxios } from "../../utils/auth";
import { toast } from "sonner";
import {
  Wifi, X, RefreshCw, Users, Clock, Globe, Smartphone, Monitor, Tablet,
  ExternalLink, Shield,
} from "lucide-react";

const TIER_PILL = {
  FREE:     "bg-white/10 text-white/60 ring-white/10",
  BRONZE:   "bg-amber-800/20 text-amber-300 ring-amber-800/40",
  SILVER:   "bg-slate-400/20 text-slate-200 ring-slate-400/40",
  GOLD:     "bg-yellow-500/20 text-yellow-300 ring-yellow-500/40",
  PLATINUM: "bg-purple-500/20 text-purple-300 ring-purple-500/40",
};

const REF_PILL = {
  Search:   "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  Social:   "bg-pink-500/15 text-pink-300 ring-pink-500/30",
  Referral: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  Direct:   "bg-white/10 text-white/60 ring-white/10",
  Email:    "bg-orange-500/15 text-orange-300 ring-orange-500/30",
  Paid:     "bg-purple-500/15 text-purple-300 ring-purple-500/30",
};

const idleColor = (s) => {
  if (s < 30) return "text-emerald-300";
  if (s < 120) return "text-amber-300";
  return "text-red-300";
};

const fmtDur = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const fmtIdle = (s) => {
  if (s == null) return "—";
  if (s < 5) return "now";
  if (s < 60) return `${s}s ago`;
  return `${Math.floor(s / 60)}m ago`;
};

const DeviceIcon = ({ type, className }) => {
  if (type === "mobile")  return <Smartphone className={className} />;
  if (type === "tablet") return <Tablet    className={className} />;
  return                        <Monitor    className={className} />;
};

const flagFor = (country) => {
  if (!country || country.length !== 2) return "";
  const cp = country.toUpperCase().split("").map((c) => 0x1F1E6 + c.charCodeAt(0) - 65);
  return String.fromCodePoint(...cp);
};

export default function OnlineNowModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/analytics/command/online-now");
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Online Now");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on open + poll every 30s while open
  useEffect(() => {
    if (!open) return;
    load();
    pollRef.current = setInterval(load, 30_000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [open, load]);

  if (!open) return null;

  const c = data?.cohort;
  const sessions = data?.sessions || [];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="online-now-modal"
    >
      <div
        className="w-full max-w-[1400px] max-h-[90vh] flex flex-col rounded-xl border border-[#C5A55A]/30 bg-[#111113] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Wifi className="w-4 h-4 text-emerald-300" />
            <h2 className="text-sm font-semibold text-white">Who's Online Right Now</h2>
            <span className="text-[11px] text-white/40">Last 5 minutes · auto-refresh 30s</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10"
              title="Refresh"
              data-testid="online-now-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10"
              data-testid="online-now-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Cohort stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-5 py-4 border-b border-white/10 bg-black/20" data-testid="online-now-stats">
          <StatTile label="Online Now" value={c?.total ?? "—"} icon={Wifi} tint="text-emerald-300" />
          <StatTile label="Signed In" value={c?.signed_in ?? "—"} icon={Shield} tint="text-[#C5A55A]"
                    hint={c ? `${c.guests} guest${c.guests === 1 ? "" : "s"}` : ""} />
          <StatTile label="Avg Duration" value={fmtDur(c?.avg_duration_seconds)} icon={Clock} tint="text-sky-300" />
          <StatTile label="Top Referrer"
                    value={c?.top_referrer?.name || "—"}
                    icon={Globe}
                    tint="text-white/80"
                    hint={c?.top_referrer?.count ? `${c.top_referrer.count} session${c.top_referrer.count === 1 ? "" : "s"}` : ""} />
          <StatTile label="Top Entry"
                    value={c?.top_entry_path?.path || "—"}
                    icon={Users}
                    tint="text-white/80"
                    hint={c?.top_entry_path?.count ? `${c.top_entry_path.count} entered here` : ""}
                    mono />
        </div>

        {/* Sessions table */}
        <div className="flex-1 overflow-auto" data-testid="online-now-table-wrap">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.03] text-white/40 uppercase tracking-wider text-[10px] sticky top-0 z-10">
              <tr>
                <th className="text-left py-2 px-3">Who</th>
                <th className="text-left py-2 px-3">Tier</th>
                <th className="text-right py-2 px-3">On for</th>
                <th className="text-right py-2 px-3">Idle</th>
                <th className="text-left py-2 px-3">Entry</th>
                <th className="text-right py-2 px-3">Pages</th>
                <th className="text-left py-2 px-3">Referrer</th>
                <th className="text-left py-2 px-3">UTM</th>
                <th className="text-left py-2 px-3">Device</th>
                <th className="text-left py-2 px-3">Geo</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 && (
                <tr><td colSpan="10" className="text-center py-12 text-white/30">Nothing active in the last 5 minutes</td></tr>
              )}
              {sessions.map((s) => (
                <tr
                  key={s.session_id}
                  className="border-t border-white/[0.04] hover:bg-white/[0.02]"
                  data-testid={`online-row-${s.session_id}`}
                >
                  <td className="py-2 px-3 min-w-[200px]">
                    {s.is_authenticated && s.user_email ? (
                      <div>
                        <div className="text-white/90 flex items-center gap-1.5">
                          {s.user_name || s.user_email.split("@")[0]}
                          {s.is_admin && <Shield className="w-3 h-3 text-[#C5A55A]" title="Admin" />}
                          {s.tab_count > 1 && (
                            <span
                              className="ml-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-mono bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30"
                              title={`${s.tab_count} active ${s.multi_device ? "devices" : "tabs"} · ${s.session_ids.length} sessions`}
                              data-testid={`tab-badge-${s.session_id}`}
                            >
                              ×{s.tab_count} {s.multi_device ? "devices" : "tabs"}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-white/40 truncate">{s.user_email}</div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-white/50 italic flex items-center gap-1.5">
                          🕵️ Guest
                          {s.tab_count > 1 && (
                            <span
                              className="inline-block px-1.5 py-0.5 rounded text-[9px] font-mono bg-white/10 text-white/60"
                              title={`${s.tab_count} tabs`}
                              data-testid={`tab-badge-${s.session_id}`}
                            >
                              ×{s.tab_count} tabs
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono text-white/30 truncate" title={s.visitor_id}>
                          {(s.visitor_id || "").slice(0, 12)}…
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {s.subscription_tier ? (
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ring-1 ${TIER_PILL[s.subscription_tier] || TIER_PILL.FREE}`}>
                        {s.subscription_tier}
                      </span>
                    ) : <span className="text-white/30">—</span>}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-white/80">{fmtDur(s.duration_seconds)}</td>
                  <td className={`py-2 px-3 text-right font-mono ${idleColor(s.idle_seconds)}`}>{fmtIdle(s.idle_seconds)}</td>
                  <td className="py-2 px-3 font-mono text-[11px] text-white/60 max-w-[180px] truncate" title={s.entry_path}>
                    {s.entry_path || "/"}
                  </td>
                  <td className="py-2 px-3 text-right text-white/60">{s.page_count || 0}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ring-1 ${REF_PILL[s.referrer_category] || REF_PILL.Direct}`}>
                        {s.referrer_category || "Direct"}
                      </span>
                      {s.referrer_domain && (
                        <span className="text-[10px] text-white/40 truncate max-w-[110px]" title={s.referrer_domain}>
                          {s.referrer_domain}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-[10px] max-w-[140px]">
                    {s.utm_source || s.utm_campaign ? (
                      <div className="text-white/60 truncate" title={`${s.utm_source} / ${s.utm_medium} / ${s.utm_campaign}`}>
                        <div>{s.utm_source || "—"}</div>
                        {s.utm_campaign && <div className="text-white/40">{s.utm_campaign}</div>}
                      </div>
                    ) : <span className="text-white/20">—</span>}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1.5 text-white/60">
                      <DeviceIcon type={s.device_type} className="w-3 h-3" />
                      <span className="text-[10px] truncate max-w-[110px]" title={`${s.browser} · ${s.os}`}>
                        {s.browser?.split(" ")[0] || s.device_type}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-[10px] text-white/60 whitespace-nowrap">
                    <span title={s.ip_masked}>{flagFor(s.geo?.country)}</span>{" "}
                    {s.geo?.country || "—"}
                    {s.geo?.city && <span className="text-white/30"> · {s.geo.city}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <footer className="px-5 py-3 border-t border-white/10 flex items-center justify-between text-[11px] text-white/40">
          <span>{sessions.length} active session{sessions.length === 1 ? "" : "s"}</span>
          <span>Deep-dive per-user command center coming next (P1)</span>
        </footer>
      </div>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tint, hint, mono }) {
  return (
    <div className="rounded-lg border border-white/[0.08] bg-[#0a0a0a] p-3" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40 mb-1">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`text-base font-semibold ${mono ? "font-mono" : ""} ${tint || "text-white"}`}>{value}</div>
      {hint && <div className="text-[10px] text-white/40 mt-0.5">{hint}</div>}
    </div>
  );
}
